/* Inspired on react-swipe-button but needed to adapt it as it had embedded styling. */
import React, { Component, memo, MouseEventHandler, ReactNode, RefObject, TouchEventHandler } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';

const slider: RefObject<HTMLDivElement | null> = React.createRef();
const container: RefObject<HTMLDivElement | null> = React.createRef();
const isTouchDevice = 'ontouchstart' in document.documentElement;

const StyledSwipeButton = styled(motion.div)``;

type SwipeButtonTestProps = {
	label?: string;
	dataAt?: string;
	buttonIcon?: ReactNode;
	startDrag?: MouseEventHandler;
	className?: string;
	onConfirm: () => void;
	sliderSize: number;
	isSmall: boolean;
	children: ReactNode;
};

const SwipeButtonTest = memo<SwipeButtonTestProps>(({ dataAt, buttonIcon, startDrag }) => {
	return (
		<StyledSwipeButton ref={container} className="swipe-button-container" data-at={dataAt}>
			<div className="swipe-button-slider" ref={slider}>
				{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
				<div
					className="panic-btn_icon"
					onMouseDown={startDrag}
					onTouchStart={startDrag as unknown as TouchEventHandler}
					role="main"
				>
					{buttonIcon}
				</div>
			</div>
		</StyledSwipeButton>
	);
});

SwipeButtonTest.displayName = 'SwipeButtonTest';

type ReactSwipeButtonProps = {
	onConfirm: () => void;
	onFailure?: () => void;
	buttonIcon?: ReactNode;
	confirmIcon?: ReactNode;
	dataAt?: string;
	label?: string;
	className?: string;
	isSmall?: boolean;
	children?: ReactNode;
};
type StateType = {
	unlocked?: boolean;
	sliderSize?: number;
};

export default class ReactSwipeButton extends Component<ReactSwipeButtonProps, StateType> {
	private isDragging = false;

	private sliderLeft = 0;

	private containerWidth = 0;

	private unmounted = false;

	private startX = 0;

	state = {
		unlocked: false,
		sliderSize: 40
	};

	componentDidMount() {
		// TODO: fix this, this is a temp hack to fix unmount issue on global menu
		this.unmounted = false;
		const panicButtonWrapper = document.querySelector('#panic-button-wrapper');
		if (isTouchDevice) {
			panicButtonWrapper?.addEventListener('touchmove', e => {
				this.onDrag(e as TouchEvent);
			});
			panicButtonWrapper?.addEventListener('touchend', this.stopDrag);
		} else {
			panicButtonWrapper?.addEventListener('mousemove', e => {
				this.onDrag(e as MouseEvent);
			});
			panicButtonWrapper?.addEventListener('mouseup', this.stopDrag);
		}
		// this.containerWidth = container.current.clientWidth - 28;
		this.setState({ sliderSize: this.props.isSmall ? 36 : 40 });
	}

	componentWillUnmount() {
		this.unmounted = true;
	}

	private onDrag = (e: TouchEvent | MouseEvent) => {
		if (this.unmounted || this.state.unlocked) return;
		if (this.containerWidth <= 0) {
			this.containerWidth = container.current?.clientWidth
				? container.current.clientWidth - this.state.sliderSize
				: 0;
		}
		if (this.isDragging) {
			if (isTouchDevice) {
				this.sliderLeft = Math.min(
					Math.max(0, (e as TouchEvent).touches[0].clientX - this.startX),
					this.containerWidth
				);
			} else {
				this.sliderLeft = Math.min(Math.max(0, (e as MouseEvent).clientX - this.startX), this.containerWidth);
			}
			this.updateSliderStyle();
		}
	};

	private updateSliderStyle = () => {
		if (this.unmounted || this.state.unlocked) return;

		if (slider.current) {
			slider.current.style.left = `${this.sliderLeft - this.state.sliderSize}px`;
		}
	};

	private stopDrag = () => {
		if (this.unmounted || this.state.unlocked) return;
		if (this.isDragging) {
			this.isDragging = false;
			if (this.sliderLeft > this.containerWidth * 0.9) {
				this.sliderLeft = this.containerWidth;
				this.onSuccess();
				if (this.props.onConfirm) {
					this.props.onConfirm();
				}
			} else {
				this.sliderLeft = 0;
				if (this.props.onFailure) {
					this.props.onFailure();
				}
			}
			this.updateSliderStyle();
		}
	};

	private startDrag = (e: TouchEvent | MouseEvent) => {
		if (this.unmounted || this.state.unlocked) return;
		this.isDragging = true;
		if (isTouchDevice) {
			this.startX = (e as TouchEvent).touches[0].clientX;
		} else {
			this.startX = (e as MouseEvent).clientX;
		}
	};

	private onSuccess = () => {
		if (container.current) {
			container.current.style.width = `${container.current.clientWidth}px`;
		}
		this.setState({
			unlocked: true
		});
	};

	render() {
		const { buttonIcon, confirmIcon, dataAt, label, className, onConfirm, isSmall, children } = this.props;
		const icon = !this.state.unlocked ? buttonIcon : confirmIcon;
		return (
			<SwipeButtonTest
				buttonIcon={icon}
				dataAt={dataAt !== undefined ? dataAt : ''}
				label={label !== undefined ? label : ''}
				startDrag={this.startDrag as unknown as MouseEventHandler}
				className={className !== undefined ? className : ''}
				onConfirm={onConfirm}
				sliderSize={this.state.sliderSize}
				isSmall={!!isSmall}
			>
				{children}
			</SwipeButtonTest>
		);
	}
}
